import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, NavigationExtras, Router} from '@angular/router';
import {combineLatest, forkJoin, Subject, Subscription} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {TrackerCandidateStatus} from '../../../model';
import {HttpError} from '../../shared/api.service';
import {AppStateService} from '../../shared/app-state.service';
import {AuthService} from '../../shared/auth.service';
import {IdentifyModalComponent} from '../../shared/identify-modal/identify-modal.component';
import {NavigationService} from '../../shared/navigation.service';
import {ClientApiService} from '../client-api.service';
import {NewRoleFormComponent} from '../new-role-form/new-role-form.component';
import * as isBefore from 'date-fns/is_before';
import * as endOfToday from 'date-fns/end_of_today';

function notEmpty(s: Submittal): boolean {
  return s && s.profile && !!s.profile.profileName;
}

@Component({
  selector: 'position-candidates-page',
  templateUrl: './position-candidates-page.component.html',
  styleUrls: ['./position-candidates-page.component.scss']
})
export class PositionCandidatesPageComponent implements OnInit, OnDestroy {

  order: Order;
  workingOrders: Order[] = [];
  acceptedOfferOrders: Order[] = [];
  submittals: Submittal[];
  TrackerCandidateStatus = TrackerCandidateStatus;
  editable = false;
  saveError = false;
  favorites: string[];
  identifyCallback: Function | null = null;
  returnTo: string | null = null;
  isAdmin$ = this.Auth.user$.pipe(map(user => user && user.admin));
  reload$ = new Subject<void>();
  currentlyWorkingExpanded = false;
  acceptedOfferExpanded = false;
  sub: Subscription;

  staff: StaffInfo[];

  @ViewChild('identifyModal') identifyModal: IdentifyModalComponent;
  @ViewChild('newRoleForm') newRoleForm: NewRoleFormComponent;

  constructor(private Api: ClientApiService,
              private Auth: AuthService,
              private Router: Router,
              private route: ActivatedRoute,
              private Navigation: NavigationService,
              private AppState: AppStateService) {
  }

  ngOnInit() {
    this.AppState.loading$.next(true);
    this.sub = combineLatest(this.route.paramMap, this.route.queryParamMap, this.reload$)
      .pipe(switchMap(([params, query]) => {
        this.returnTo = query.get('returnTo');
        return forkJoin(
          this.Api.getSubmittals(params.get('id')),
          this.Api.getOrder(params.get('id')),
          this.Api.getChildOrders(params.get('id')),
          this.Api.getFavorites()
        );
      }))
      .subscribe(([submittals, order, childOrders, favorites]) => {
        if (order.talentPool) {
          return this.Router.navigate(['pools', order.orderId], {replaceUrl: true});
        } else if (order.talent && !(order.openings > 1 && order.openings > order.workingCandidates)) {
          return this.Router.navigate(['positions', order.orderId, 'details'], {replaceUrl: true, queryParamsHandling: 'merge'});
        }
        this.staff = submittals.staff;
        this.order = order;
        this.favorites = favorites;
        this.AppState.loading$.next(false);
        this.workingOrders = this.getWorkingOrders(childOrders);
        this.acceptedOfferOrders = this.getAcceptedOfferOrders(childOrders);
        this.submittals = this.filterSubmittals(submittals);
      }, (err: HttpError) => {
        if (err && err.status === 404) {
          return this.Navigation.handleNotFound();
        } else {
          return this.Navigation.handleError(err);
        }
      });
    this.reload();
    this.Auth.user$.subscribe(user => this.editable = user && user.admin);
  }

  ngOnDestroy() {
    if (this.sub) this.sub.unsubscribe();
  }

  getWorkingOrders(childOrders: Order[]) {
    return childOrders.filter(order => order.talent && isBefore(order.startDate, endOfToday()));
  }

  getAcceptedOfferOrders(childOrders: Order[]) {
    return childOrders.filter(order => order.talent && !isBefore(order.startDate, endOfToday()));
  }

  filterSubmittals(submittals: Submittals) {
    return submittals.submittals.filter(s => notEmpty(s)
      && !this.workingOrders.find(order => order.talent.id === s.personId)
      && !this.acceptedOfferOrders.find(order => order.talent.id === s.personId)
    );
  }

  checkIdentity(): boolean {
    if (!this.Auth.isFullyAuthenticated()) {
      this.identifyCallback = () => {
        if (this.Auth.isFullyAuthenticated()) {
          this.goAllPositions();
        }
      };
      this.identifyModal.show();
      return false;
    }
    return true;
  }

  goAllPositions() {
    if (this.checkIdentity()) {
      if (this.returnTo === 'positions') {
        this.Router.navigate(['/positions']);
      } else {
        this.Navigation.goHome();
      }
    }
  }

  unauthenticatedOrderClicked(route: {commands: any[], extras: NavigationExtras}) {
    this.identifyCallback = () => {
      if (this.Auth.isFullyAuthenticated()) {
        this.Router.navigate(route.commands, route.extras);
      }
    };

    this.identifyModal.show();
  }

  savePositionTitle(value: string) {
    this.Api.saveTitle(this.order.orderId, value)
      .subscribe(() => this.order.position = value,
        err => {
          console.error('Failed to save position title.', err);
          this.saveError = true;
        });
  }

  reload() {
    this.reload$.next();
  }

  currentlyWorkingClicked() {
    if (this.workingOrders.length) {
      this.currentlyWorkingExpanded = !this.currentlyWorkingExpanded;
    }
  }

  acceptedOfferClicked() {
    if (this.acceptedOfferOrders.length) {
      this.acceptedOfferExpanded = !this.acceptedOfferExpanded;
    }
  }
}
